@if (loading()) {
  <div
    class="eventq-site-overview-maps eventq-site-overview-maps__skeleton"
    aria-hidden="true"
  >
    <div class="eventq-site-overview-maps__skeleton-header">
      <div class="eventq-site-overview-maps__skeleton-toggle"></div>
      <div class="eventq-site-overview-maps__skeleton-toggle eventq-site-overview-maps__skeleton-toggle--short"></div>
    </div>
    <div class="eventq-site-overview-maps__skeleton-viewport"></div>
    <div class="eventq-site-overview-maps__skeleton-thumbs">
      @for (thumb of [1, 2, 3]; track thumb) {
        <div class="eventq-site-overview-maps__skeleton-thumb"></div>
      }
    </div>
  </div>
} @else if (hasContent()) {
  <div class="eventq-site-overview-maps">
    <div class="eventq-site-overview-maps__header">
      <div class="eventq-site-overview-maps__toggles" role="group" aria-label="Map type">
        <button
          type="button"
          class="eventq-site-overview-maps__toggle"
          [class.eventq-site-overview-maps__toggle--active]="activeMode() === 'property'"
          (click)="selectMode('property')"
        >
          <span
            class="eventq-site-overview-maps__toggle-icon"
            [style.mask-image]="iconMask(propertyMapIconSrc())"
            [style.-webkit-mask-image]="iconMask(propertyMapIconSrc())"
            aria-hidden="true"
          ></span>
          <span>Property Map</span>
        </button>
        <button
          type="button"
          class="eventq-site-overview-maps__toggle"
          [class.eventq-site-overview-maps__toggle--active]="activeMode() === 'google'"
          (click)="selectMode('google')"
        >
          <span
            class="eventq-site-overview-maps__toggle-icon"
            [style.mask-image]="iconMask(googleMapIconSrc())"
            [style.-webkit-mask-image]="iconMask(googleMapIconSrc())"
            aria-hidden="true"
          ></span>
          <span>Google Map</span>
        </button>
      </div>
    </div>

    <div
      #viewport
      class="eventq-site-overview-maps__viewport"
      [class.eventq-site-overview-maps__viewport--interactive]="isPropertyInteractive()"
      [class.eventq-site-overview-maps__viewport--panning]="viewportPanning()"
      (pointerenter)="onViewportPointerEnter()"
      (pointerleave)="onViewportPointerLeave()"
    >
      @if (googleUnavailableMessage(); as unavailable) {
        <p class="eventq-site-overview-maps__unavailable">{{ unavailable }}</p>
      } @else if (showInteractiveGoogleMap()) {
        @if (googleMapLoading()) {
          <div
            class="eventq-site-overview-maps__google-skeleton"
            aria-hidden="true"
          ></div>
        }
        @if (googleApiState() === 'ready') {
          <google-map
            class="eventq-site-overview-maps__google-map"
            width="100%"
            height="100%"
            [style.position]="'absolute'"
            [style.top.px]="0"
            [style.right.px]="0"
            [style.bottom.px]="0"
            [style.left.px]="0"
            [center]="googleMapCenter()"
            [zoom]="18"
            [options]="googleMapOptions"
            (mapInitialized)="onGoogleMapInitialized($event)"
            (authFailure)="onGoogleMapAuthFailure()"
          >
            <map-marker [position]="googleMapCenter()" />
          </google-map>
          <button
            type="button"
            class="eventq-site-overview-maps__center-map"
            (click)="centerGoogleMap()"
            (pointerdown)="$event.stopPropagation()"
          >
            Center Map
          </button>
        }
      } @else if (activeImageSrc()) {
        <img
          class="eventq-site-overview-maps__image"
          [src]="activeImageSrc() | prefixUrl"
          alt=""
          draggable="false"
          [style.transform]="imageTransform()"
          (pointerdown)="onImagePointerDown($event)"
          (pointermove)="onImagePointerMove($event)"
          (pointerup)="onImagePointerUp($event)"
          (pointercancel)="onImagePointerUp($event)"
        />
      }

      @if (showNav()) {
        <button
          type="button"
          class="eventq-site-overview-maps__control eventq-site-overview-maps__prev"
          aria-label="Previous property map"
          (click)="goPrev()"
          (pointerdown)="$event.stopPropagation()"
        >
          <img
            class="eventq-site-overview-maps__control-icon"
            [src]="controlIcons.prev | prefixUrl"
            alt=""
            width="32"
            height="32"
          />
        </button>
        <button
          type="button"
          class="eventq-site-overview-maps__control eventq-site-overview-maps__next"
          aria-label="Next property map"
          (click)="goNext()"
          (pointerdown)="$event.stopPropagation()"
        >
          <img
            class="eventq-site-overview-maps__control-icon"
            [src]="controlIcons.next | prefixUrl"
            alt=""
            width="32"
            height="32"
          />
        </button>
      }

      @if (showViewportControls()) {
        <div class="eventq-site-overview-maps__zoom-stack" role="group" aria-label="Map zoom">
          <button
            type="button"
            class="eventq-site-overview-maps__control eventq-site-overview-maps__zoom-in"
            aria-label="Zoom in"
            [disabled]="!canZoomIn()"
            (click)="zoomIn()"
            (pointerdown)="$event.stopPropagation()"
          >
            <img
              class="eventq-site-overview-maps__control-icon"
              [src]="controlIcons.zoomIn | prefixUrl"
              alt=""
              width="32"
              height="32"
            />
          </button>
          <button
            type="button"
            class="eventq-site-overview-maps__control eventq-site-overview-maps__zoom-out"
            aria-label="Zoom out"
            [disabled]="!canZoomOut()"
            (click)="zoomOut()"
            (pointerdown)="$event.stopPropagation()"
          >
            <img
              class="eventq-site-overview-maps__control-icon"
              [src]="controlIcons.zoomOut | prefixUrl"
              alt=""
              width="32"
              height="32"
            />
          </button>
          <button
            type="button"
            class="eventq-site-overview-maps__control eventq-site-overview-maps__reset"
            aria-label="Reset map view"
            (click)="resetView()"
            (pointerdown)="$event.stopPropagation()"
          >
            <img
              class="eventq-site-overview-maps__control-icon"
              [src]="controlIcons.reset | prefixUrl"
              alt=""
              width="32"
              height="32"
            />
          </button>
        </div>
      }
    </div>

    @if (showThumbnails()) {
      <div class="eventq-site-overview-maps__thumbnails" role="listbox" aria-label="Property maps">
        @for (map of propertyMaps(); track map.id; let index = $index) {
          <button
            type="button"
            class="eventq-site-overview-maps__thumbnail"
            [class.eventq-site-overview-maps__thumbnail--active]="isThumbnailActive(index)"
            role="option"
            [attr.aria-selected]="isThumbnailActive(index)"
            (click)="selectPropertyIndex(index)"
          >
            <img [src]="map.src | prefixUrl" alt="" />
          </button>
        }
      </div>
    }
  </div>
} @else {
  <div class="eventq-site-overview-maps eventq-site-overview-maps--empty">
    <p class="eventq-site-overview-maps__empty">{{ emptyMessage() }}</p>
  </div>
}
